// GauntletReport.tsx, post-run summary: waves, rewards, party performance
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import styles from './GauntletRun.module.css';
import reportStyles from './GauntletHistory.module.css';
import { formatDateTime as fmtDT } from '../../../utils/dateFormat';

interface WaveReport {
  id: number;
  wave: number;
  battleReportId: number;
  battleReport: {
    id: number;
    title: string;
    winner: string;
  } | null;
}

interface RunParticipant {
  id: number;
  mercenaryId: number;
  hasDroppedOut: boolean;
  droppedOutAfterWave: number | null;
  rewardSilver: number;
  rewardXp: number;
  rewardItems: number;
  mercenary: { id: number; name: string; level: number; race: string };
}

interface RunData {
  id: number;
  bracket: string;
  status: string;
  currentWave: number;
  accumulatedSilver: number;
  accumulatedXp: number;
  startedAt: string | null;
  completedAt: string | null;
  participants: RunParticipant[];
}

const BRACKET_LABEL: Record<string, string> = {
  iron: 'Iron', blood: 'Blood', legend: 'Legend',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return fmtDT(iso);
}

const GauntletReport: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const [reports, setReports] = useState<WaveReport[]>([]);
  const [run, setRun] = useState<RunData | null>(null);
  const [selectedWave, setSelectedWave] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { mercenary } = useMercenary();

  useEffect(() => {
    Promise.all([
      axios.get(`/api/gauntlet/runs/${runId}/reports`),
      axios.get(`/api/gauntlet/runs/${runId}`),
    ]).then(([reportsRes, runRes]) => {
      setReports(reportsRes.data);
      setRun(runRes.data);
      if (reportsRes.data.length > 0) setSelectedWave(reportsRes.data[0].wave);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [runId]);

  if (loading) return <div className={styles.loading}>Loading reports…</div>;

  const wavesCompleted = run
    ? run.status === 'completed' ? 5 : Math.max(0, run.currentWave - (run.status === 'failed' ? 1 : 0))
    : 0;

  return (
    <div className={styles.reportContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.reportTitle}>
          {run ? `${BRACKET_LABEL[run.bracket] ?? run.bracket} Gauntlet` : 'Gauntlet'}, Wave Reports
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className={styles.backBtn} onClick={() => navigate('/gauntlet/history')}>⚔ History</button>
          <button className={styles.backBtn} onClick={() => navigate('/gauntlet')}>← Hub</button>
        </div>
      </div>

      {/* Run summary */}
      {run && (
        <div className={styles.reportCard} style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className={reportStyles.statusBadge} data-status={run.status}>
              {run.status === 'completed' ? '✓ Completed' : run.status === 'failed' ? '✗ Failed' : run.status}
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {wavesCompleted} / 5 waves cleared
            </span>
            {run.completedAt && (
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                {formatDate(run.completedAt)}
              </span>
            )}
          </div>

          {/* Wave dots */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '0.7rem' }}>
            {[1,2,3,4,5].map(w => {
              const done = w <= wavesCompleted;
              const isFail = w === run.currentWave && run.status === 'failed';
              return (
                <div
                  key={w}
                  className={`${reportStyles.waveDot} ${done ? reportStyles.done : ''} ${isFail ? reportStyles.failed : ''}`}
                  title={w === 5 ? 'Boss wave' : `Wave ${w}`}
                >
                  {w}
                </div>
              );
            })}
          </div>

          {/* Rewards */}
          {(run.accumulatedSilver > 0 || run.accumulatedXp > 0) && (
            <div className={reportStyles.rewardRow} style={{ marginBottom: '0.7rem' }}>
              {run.accumulatedSilver > 0 && (
                <div className={reportStyles.rewardItem}>
                  <strong>{run.accumulatedSilver.toLocaleString()}</strong> silver (party total)
                </div>
              )}
              {run.accumulatedXp > 0 && (
                <div className={reportStyles.rewardItem}>
                  <strong>{run.accumulatedXp.toLocaleString()}</strong> XP (party total)
                </div>
              )}
            </div>
          )}

          {/* Participant table */}
          {run.participants.length > 0 && (
            <table className={reportStyles.participantTable}>
              <thead>
                <tr>
                  <th>Mercenary</th>
                  <th>Level</th>
                  <th>Waves Survived</th>
                  <th>Outcome</th>
                  <th>Silver</th>
                  <th>XP</th>
                  <th>Items</th>
                </tr>
              </thead>
              <tbody>
                {run.participants.map(p => {
                  const pWaves = p.hasDroppedOut
                    ? (p.droppedOutAfterWave ?? 0)
                    : wavesCompleted;
                  return (
                    <tr key={p.id}>
                      <td>
                        <Link to={`/mercenary/${p.mercenary.id}`}>{p.mercenary.name}</Link>
                        {mercenary && p.mercenaryId === mercenary.id && (
                          <span className={reportStyles.droppedBadge}> (you)</span>
                        )}
                      </td>
                      <td>Lv.{p.mercenary.level}</td>
                      <td>{pWaves}</td>
                      <td>
                        {p.hasDroppedOut
                          ? <span style={{ color: '#e05c4a' }}>Dropped out</span>
                          : <span style={{ color: '#4caf72' }}>Survived</span>
                        }
                      </td>
                      <td>{p.rewardSilver ?? 0}</td>
                      <td>{p.rewardXp ?? 0}</td>
                      <td>{p.rewardItems ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Wave tabs */}
      {reports.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No wave battle reports recorded for this run.</p>
      ) : (
        <>
          <div className={styles.waveTabs}>
            {reports.map(r => (
              <button
                key={r.wave}
                className={`${styles.waveTab} ${selectedWave === r.wave ? styles.waveTabActive : ''}`}
                onClick={() => setSelectedWave(r.wave)}
              >
                Wave {r.wave}
                {r.battleReport && (
                  <span style={{ marginLeft: '5px', fontSize: '0.7rem' }}>
                    {r.battleReport.winner === 'teamOne' ? '✓' : '✗'}
                  </span>
                )}
              </button>
            ))}
          </div>

          {(() => {
            const report = reports.find(r => r.wave === selectedWave);
            if (!report) return <p style={{ color: 'var(--text-muted)' }}>No report for wave {selectedWave}.</p>;
            return (
              <div className={styles.reportCard}>
                <h3>{report.battleReport?.title ?? `Wave ${report.wave}`}</h3>
                <p>
                  Result:{' '}
                  <strong style={{ color: report.battleReport?.winner === 'teamOne' ? '#4caf72' : '#e05c4a' }}>
                    {report.battleReport?.winner === 'teamOne' ? 'Party Won' : 'Party Fell'}
                  </strong>
                </p>
                <Link
                  to={`/battle-result/${report.battleReportId}`}
                  className={styles.reportLink}
                >
                  View Full 2D Battle Report
                </Link>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default GauntletReport;
